import { Spacing } from '@vkontakte/vkui';
import { xray } from 'lib/xray';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatureQuotaCleanerGroupHints, getFeatureQuotaCleanerGroupTitles } from 'reactApp/modules/features/features.selectors';
import { routerStart } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { hasMailSubscription } from 'reactApp/modules/subscriptions/subscriptions.selectors';
import { widgetTitles } from 'reactApp/modules/userQuotaCleaner/helpers/constants';
import { getCurrentGroup, getCurrentGroupTotalCount } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { sendQuotaCleanerGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import { Header } from 'reactApp/ui/QuotaCleanerPage/Header/Header';
import { url } from 'reactApp/ui/QuotaCleanerPage/QuotaCleanerPage.data';
import { QuotaCleanerSpacePromo } from 'reactApp/ui/QuotaCleanerSpacePromo/QuotaCleanerSpacePromo';
import { Toolbar } from 'reactApp/ui/Toolbar/Toolbar';
import { ToolbarGapMode } from 'reactApp/ui/Toolbar/Toolbar.types';
import { noop } from 'reactApp/utils/helpers';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import styles from './QuotaCleanerPage.css';

/**
 * Страница чистилки Единой квоты
 * Функционал очистки per группа
 */
export const QuotaCleanerPage: FC = () => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [scrollRef, setScrollRef] = useState(null);
    const updateScrollRef = useCallback((node) => {
        setScrollRef(node);
    }, []);

    const dispatch = useDispatch();
    const currentGroup = useSelector(getCurrentGroup);
    const isPaidUser = useSelector(hasMailSubscription);
    const totalCount = useSelector(getCurrentGroupTotalCount);
    const priorityGroupTitles = useSelector(getFeatureQuotaCleanerGroupTitles);
    const priorityGroupHints = useSelector(getFeatureQuotaCleanerGroupHints);

    const onItemSelect = useCallback(() => {
        sendQuotaCleanerGa({ action: 'one-file-select' });
    }, []);

    useEffect(() => xray.send('quota-cln-page-show'), []);

    useEffect(() => {
        if (!currentGroup?.isLoaded) {
            return;
        }

        const { groupId, size } = currentGroup || {};
        xray.send('quota-cln-curr-grp', { i: groupId });
        sendQuotaCleanerGa({ action: 'cleaner_show_dtld', dwh: { name_block: groupId, size_block: size, count_block: totalCount } });
    }, [currentGroup?.isLoaded]);

    useEffect(() => {
        dispatch(
            routerStart({
                id: url,
                params: {},
                storage: EStorageType.quotaCleaner,
                url,
                __isFolder: false,
                __parent: '/',
            })
        );
    }, [dispatch]);

    // to disable scroll of background elements
    useEffect(() => {
        const el = ref?.current;
        scrollLock(el);
        return () => scrollUnlock(el);
    }, [ref]);

    return (
        <div ref={ref}>
            {currentGroup && (
                <div className={styles.wrapper} ref={updateScrollRef} data-qa-id="quota-cleaner-page">
                    <div className={styles.toolbar}>
                        <Toolbar gapMode={ToolbarGapMode.wide} />
                        <div data-promo-target="quota-cleaner-toolbar" />
                    </div>
                    {!isPaidUser && <QuotaCleanerSpacePromo />}
                    <div className={styles.list}>
                        <Spacing size={32} />
                        <Header
                            title={priorityGroupTitles[currentGroup.groupId] || widgetTitles[currentGroup.groupId]}
                            hint={priorityGroupHints[currentGroup.groupId]}
                            size={currentGroup.size ?? 0}
                        />
                        <Spacing size={32} />
                        <DataList
                            goTo={noop}
                            storage={currentGroup.storage}
                            customScrollElement={scrollRef}
                            onItemSelectCallback={onItemSelect}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
